import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { PropertiesService } from './properties.service';
import { ButtonModule } from 'primeng/button';
import { ImageModule } from 'primeng/image';
import { CardModule } from 'primeng/card';
import {
  Category,
  InvestementStatus,
  InvestmentStrategyType,
  Property,
  PropertyParams,
  State,
} from './properties.model';
import { Subscription } from 'rxjs';
import { ProgressBarModule } from 'primeng/progressbar';
import { DividerModule } from 'primeng/divider';
import { SvgIconComponent } from 'angular-svg-icon';
import { CarouselModule } from 'primeng/carousel';
import { SkeletonModule } from 'primeng/skeleton';
import { TablistComponent } from '@shared/components/tablist/tablist.component';
import { NgProgressService } from '@shared/services/ng-progress.service';
import { ProfileService } from '../profile/profile.service';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';
import { DialogModule } from 'primeng/dialog';
import { PopoverModule } from 'primeng/popover';
import { MultiSelectModule } from 'primeng/multiselect';
import { TablerIconComponent } from 'angular-tabler-icons';

@Component({
  selector: 'app-properties',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    ImageModule,
    CardModule,
    ProgressBarModule,
    DividerModule,
    SvgIconComponent,
    CarouselModule,
    SkeletonModule,
    TablistComponent,
    CustomCurrencyPipe,
    DialogModule,
    ButtonModule,
    PopoverModule,
    MultiSelectModule,
    FormsModule,
    TablerIconComponent,
  ],
  templateUrl: './properties.component.html',
  styleUrl: './properties.component.css',
})
export class PropertiesComponent implements OnInit, OnDestroy {
  @Input() showBookmarkedProperties: boolean = false;

  properties: Property[] = [];
  activeTab: number = 0;
  currency: string | undefined;
  showWelcomeDialog: boolean = false;
  categoryEnum = Category;
  InvestmentStrategyTypeEnum = InvestmentStrategyType;
  InvestementStatus = InvestementStatus;
  readonly tabs: string[] = [
    'Available', // InvestementStatus.AVAILABLE
    'Funded', // InvestementStatus.FUNDED,
    'Exited', // InvestementStatus.EXITED,
  ];
  State = State;

  // Filter values
  appliedFiltersCount: number = 0;
  investementStrategyValues: {
    name: string;
    value: InvestmentStrategyType;
  }[] = [];
  propertyStateValues: {
    name: string;
    value: State;
  }[] = [];
  propertyCategoryValues: {
    name: string;
    value: Category;
  }[] = [];

  investementStrategyOptions: {
    name: string;
    value: InvestmentStrategyType;
  }[] = [
    { name: 'Balanced', value: this.InvestmentStrategyTypeEnum.BALANCED },
    {
      name: 'Capital Growth',
      value: this.InvestmentStrategyTypeEnum.CAPITAL_GROWTH,
    },
    { name: 'Fix & Flip', value: this.InvestmentStrategyTypeEnum.FIX_N_FLIP },
    { name: 'Golden Visa', value: this.InvestmentStrategyTypeEnum.GOLDEN_VISA },
    { name: 'High Yield', value: this.InvestmentStrategyTypeEnum.HIGH_YIELD },
  ];
  propertyStateOptions: {
    name: string;
    value: State;
  }[] = [
    { name: 'Operational', value: this.State.OPERATIONAL },
    { name: 'Construction', value: this.State.CONSTRUCTION },
    { name: 'Ready', value: this.State.READY },
    { name: 'Renovation', value: this.State.RENOVATION },
    { name: 'Sold', value: this.State.SOLD },
  ];
  propertyCategoryOptions: {
    name: string;
    value: Category;
  }[] = [
    { name: 'Residential', value: this.categoryEnum.RESIDENTIAL },
    { name: 'Commercial', value: this.categoryEnum.COMMERCIAL },
    { name: 'Industrial', value: this.categoryEnum.INDUSTRIAL },
    {
      name: 'Land',
      value: this.categoryEnum.LAND,
    },
    { name: 'Energy', value: this.categoryEnum.ENERGY },
    { name: 'Transportation', value: this.categoryEnum.TRANSPORTATION },
  ];

  isLoading: boolean = false;

  private isLoadingSub: Subscription = Subscription.EMPTY;
  private propertiesSub: Subscription = Subscription.EMPTY;

  readonly _ngProgressService = inject(NgProgressService);
  private readonly _propertiesService = inject(PropertiesService);
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _profileService = inject(ProfileService);

  public cardStyle: { [key: string]: string } = {};

  private setCardStyle(width: number): void {
    if (width < 640) {
      this.cardStyle = {
        'max-width': '390px',
        height: '100%',
        overflow: 'hidden',
        cursor: 'pointer',
      };
    } else {
      this.cardStyle = {
        'max-width': '390px',
        height: '100%',
        overflow: 'hidden',
        cursor: 'pointer',
      };
    }
  }

  constructor() {
    this.showWelcomeDialog =
      localStorage.getItem('showWelcomeDialog') !== 'true';

    localStorage.setItem('showWelcomeDialog', 'true');
  }

  ngOnInit(): void {
    this.setCardStyle(window.innerWidth);

    window.addEventListener('resize', () => {
      this.setCardStyle(window.innerWidth);
    });

    this.currency = this._profileService.profile.currency;

    this.isLoadingSub = this._ngProgressService.loading$.subscribe((res) => {
      this.isLoading = res;
    });

    this._propertiesService.getBookmarkedProperties();

    if (this.showBookmarkedProperties) {
      this.propertiesSub =
        this._propertiesService.bookmarkedProperties$.subscribe((res) => {
          this.properties = res.map(
            (bookmarkedProperty) => bookmarkedProperty.property
          );
        });
    } else {
      this._activatedRoute.queryParams.subscribe((queryParams) => {
        // Get the query params from the URL
        const investmentStrategyTypesParam =
          this._activatedRoute.snapshot.queryParamMap.get(
            'investmentStrategyTypes'
          );
        const statesParam =
          this._activatedRoute.snapshot.queryParamMap.get('states');
        const categoriesParam =
          this._activatedRoute.snapshot.queryParamMap.get('categories');

        // Parse the query params into arrays
        const investmentStrategyTypes = investmentStrategyTypesParam
          ? investmentStrategyTypesParam.split(',')
          : [];
        const states = statesParam ? statesParam.split(',') : [];
        const categories = categoriesParam ? categoriesParam.split(',') : [];

        // Set the filter values based on the query params
        this.appliedFiltersCount = 0;
        if (investmentStrategyTypes.length > 0) {
          this.appliedFiltersCount++;
        }

        if (states.length > 0) {
          this.appliedFiltersCount++;
        }

        if (categories.length > 0) {
          this.appliedFiltersCount++;
        }

        // Set the filter values for the multi-select components
        this.investementStrategyValues = this.investementStrategyOptions.filter(
          (option) => investmentStrategyTypes.includes(option.value)
        );
        this.propertyStateValues = this.propertyStateOptions.filter((option) =>
          states.includes(option.value)
        );
        this.propertyCategoryValues = this.propertyCategoryOptions.filter(
          (option) => categories.includes(option.value)
        );

        const params: PropertyParams = {
          status: queryParams['status'],
          states: states,
          categories: categories,
          investmentStrategyTypes: investmentStrategyTypes,
          page: 0,
          itemsPerPage: 100,
        };

        this._propertiesService.getProperties(params);

        // Set the active tab based on the status in the query params
        this.activeTab = this.tabs.findIndex(
          (tab) => tab.toLowerCase() === params.status.toLowerCase()
        );
      });

      this.propertiesSub = this._propertiesService.properties$.subscribe(
        (res) => {
          this.properties = res;
        }
      );
    }
  }

  onPropertyView(id: string): void {
    this._router.navigate(['/home/properties/view', id]);
  }

  handleClick(id: string, event: Event): void {
    // In this case redirect to property view page
    if ((event.target as any).tagName === 'IMG') {
      this.onPropertyView(id);
    }

    event.stopPropagation();
  }

  onActiveTabChange(activeTab: number): void {
    this.activeTab = activeTab;

    const status = this.tabs[activeTab].toLowerCase();

    this._router.navigate(['/home/properties'], {
      queryParams: { status: status },
    });
  }

  onViewProperties(): void {
    this._router.navigate(['/home/properties']);
  }

  onInvestmentStrategyChange($event: any): void {
    const filters = $event.value.map((item: any) => item.value).join(',');

    this._router.navigate(['/home/properties'], {
      queryParams: { investmentStrategyTypes: filters },
      queryParamsHandling: 'merge',
    });
  }

  onPropertyStateChange($event: any): void {
    const filters = $event.value.map((item: any) => item.value).join(',');

    this._router.navigate(['/home/properties'], {
      queryParams: { states: filters },
      queryParamsHandling: 'merge',
    });
  }

  onPropertyCategoryChange($event: any): void {
    const filters = $event.value.map((item: any) => item.value).join(',');

    this._router.navigate(['/home/properties'], {
      queryParams: { categories: filters },
      queryParamsHandling: 'merge',
    });
  }

  resetFilters(): void {
    this.investementStrategyValues = [];
    this.propertyStateValues = [];
    this.propertyCategoryValues = [];

    this._propertiesService.getProperties({
      status: this.tabs[this.activeTab].toLowerCase(),
      states: [],
      categories: [],
      investmentStrategyTypes: [],
      page: 0,
      itemsPerPage: 100,
    });

    this._router.navigate(['/home/properties'], {
      queryParams: { categories: '', states: '', investmentStrategyTypes: '' },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    this.propertiesSub.unsubscribe();
    this.isLoadingSub.unsubscribe();
  }
}
